/**
 * Metabolic Intake Template
 * 
 * GLP-1 Agonists (Semaglutide, Tirzepatide) and weight management protocols
 * Critical safety screening for MTC/MEN2 contraindications
 */

import { IntakeTemplate, IntakeSection, IntakeQuestion } from './types';

// ============================================================================
// Critical Safety Screening - MTC/MEN2
// ============================================================================

const SAFETY_SCREENING_QUESTIONS: IntakeQuestion[] = [
  {
    id: 'mtc_personal_history',
    text: 'Have you ever been diagnosed with medullary thyroid carcinoma (MTC)?',
    inputType: 'boolean',
    required: true,
    helpText: 'CRITICAL: GLP-1 medications are contraindicated if you have MTC'
  },
  {
    id: 'men2_history',
    text: 'Have you been diagnosed with Multiple Endocrine Neoplasia syndrome type 2 (MEN2)?',
    inputType: 'boolean',
    required: true,
    helpText: 'CRITICAL: GLP-1 medications are contraindicated with MEN2'
  },
  {
    id: 'family_mtc_history',
    text: 'Does anyone in your family have a history of medullary thyroid carcinoma?',
    inputType: 'boolean',
    required: true,
    helpText: 'Family history may be a contraindication'
  },
  {
    id: 'thyroid_nodules',
    text: 'Do you have any thyroid nodules or thyroid tumors?',
    inputType: 'boolean',
    required: true
  },
  {
    id: 'pancreatitis_history',
    text: 'Have you ever had acute pancreatitis?',
    inputType: 'boolean',
    required: true,
    helpText: 'Important for GLP-1 safety assessment'
  },
  {
    id: 'gallbladder_disease',
    text: 'Do you have gallstones or history of gallbladder disease?',
    inputType: 'boolean',
    required: true,
    helpText: 'GLP-1s may increase gallbladder complications'
  }
];

// ============================================================================
// Metabolic Assessment
// ============================================================================

const METABOLIC_ASSESSMENT_QUESTIONS: IntakeQuestion[] = [
  {
    id: 'current_weight',
    text: 'What is your current weight (in pounds)?',
    inputType: 'number',
    required: true,
    validationRules: [
      { type: 'min', value: 50, message: 'Please enter a valid weight' },
      { type: 'max', value: 700, message: 'Please enter a valid weight' }
    ]
  },
  {
    id: 'height_feet',
    text: 'What is your height (feet)?',
    inputType: 'select',
    options: ['4', '5', '6', '7'],
    required: true
  },
  {
    id: 'height_inches',
    text: 'Height (inches):',
    inputType: 'select',
    options: ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11'],
    required: true
  },
  {
    id: 'weight_goal',
    text: 'What is your weight loss goal?',
    inputType: 'select',
    options: [
      '10-20 lbs',
      '20-30 lbs',
      '30-50 lbs',
      '50-75 lbs',
      '75-100 lbs',
      '100+ lbs'
    ],
    required: true
  },
  {
    id: 'weight_loss_attempts',
    text: 'Have you tried to lose weight before?',
    inputType: 'select',
    options: [
      'No, this is my first attempt',
      'Yes, with diet alone',
      'Yes, with diet and exercise',
      'Yes, with other medications',
      'Yes, multiple methods'
    ],
    required: true
  },
  {
    id: 'weight_loss_medications',
    text: 'Have you used any weight loss medications before?',
    inputType: 'multiselect',
    options: [
      'None',
      'Phentermine',
      'Contrave',
      'Qsymia',
      'Saxenda (liraglutide)',
      'Wegovy (semaglutide)',
      'Ozempic (semaglutide)',
      'Mounjaro/Zepbound (tirzepatide)',
      'Other GLP-1',
      'Other'
    ]
  },
  {
    id: 'previous_medication_experience',
    text: 'If you have used weight loss medications, please describe your experience:',
    inputType: 'text',
    conditionalOn: {
      questionId: 'weight_loss_medications',
      operator: 'notEquals',
      value: 'None'
    }
  }
];

// ============================================================================
// Diabetes & Metabolic Conditions
// ============================================================================

const DIABETES_QUESTIONS: IntakeQuestion[] = [
  {
    id: 'diabetes_status',
    text: 'Do you have diabetes?',
    inputType: 'select',
    options: ['No', 'Pre-diabetes', 'Type 2 Diabetes', 'Type 1 Diabetes'],
    required: true
  },
  {
    id: 'diabetes_medications',
    text: 'If diabetic, what medications do you take?',
    inputType: 'multiselect',
    options: [
      'N/A - No diabetes',
      'Metformin',
      'Insulin',
      'Sulfonylureas (Glipizide, Glyburide)',
      'SGLT2 inhibitors (Jardiance, Farxiga)',
      'Other'
    ],
    conditionalOn: {
      questionId: 'diabetes_status',
      operator: 'notEquals',
      value: 'No'
    },
    helpText: 'Important: May need dose adjustment when starting GLP-1'
  },
  {
    id: 'hypoglycemia_history',
    text: 'Have you experienced low blood sugar (hypoglycemia)?',
    inputType: 'boolean',
    conditionalOn: {
      questionId: 'diabetes_status',
      operator: 'notEquals',
      value: 'No'
    }
  },
  {
    id: 'latest_a1c',
    text: 'What was your last HbA1c result (if known)?',
    inputType: 'text',
    helpText: 'Example: 6.5% or "unknown"'
  }
];

// ============================================================================
// Medical History
// ============================================================================

const MEDICAL_HISTORY_QUESTIONS: IntakeQuestion[] = [
  {
    id: 'kidney_disease',
    text: 'Do you have kidney disease or reduced kidney function?',
    inputType: 'boolean',
    required: true,
    helpText: 'May affect dosing decisions'
  },
  {
    id: 'liver_disease',
    text: 'Do you have liver disease?',
    inputType: 'boolean',
    required: true
  },
  {
    id: 'heart_disease',
    text: 'Do you have any heart disease or history of heart attack?',
    inputType: 'boolean',
    required: true
  },
  {
    id: 'diabetic_retinopathy',
    text: 'If diabetic, do you have diabetic retinopathy (eye disease)?',
    inputType: 'boolean',
    helpText: 'Important monitoring consideration'
  },
  {
    id: 'gastroparesis',
    text: 'Do you have gastroparesis (delayed stomach emptying)?',
    inputType: 'boolean',
    required: true,
    helpText: 'May affect GLP-1 tolerability'
  },
  {
    id: 'eating_disorder_history',
    text: 'Do you have a history of eating disorders?',
    inputType: 'boolean',
    required: true
  },
  {
    id: 'current_medications',
    text: 'List all current medications:',
    inputType: 'text',
    required: true
  },
  {
    id: 'allergies',
    text: 'Do you have any drug allergies?',
    inputType: 'text',
    required: true
  }
];

// ============================================================================
// Symptoms & Concerns
// ============================================================================

const SYMPTOMS_QUESTIONS: IntakeQuestion[] = [
  {
    id: 'gi_issues',
    text: 'Do you currently experience any GI issues?',
    inputType: 'multiselect',
    options: [
      'None',
      'Nausea',
      'Constipation',
      'Diarrhea',
      'Acid reflux/GERD',
      'Bloating'
    ],
    helpText: 'GLP-1s may cause GI side effects'
  },
  {
    id: 'energy_level',
    text: 'How would you rate your current energy level?',
    inputType: 'scale',
    options: ['Very Low', 'Low', 'Moderate', 'Good', 'Excellent']
  },
  {
    id: 'appetite_level',
    text: 'How would you describe your appetite?',
    inputType: 'select',
    options: [
      'Very low - rarely hungry',
      'Low - occasional hunger',
      'Normal',
      'High - frequently hungry',
      'Very high - always hungry'
    ],
    required: true
  },
  {
    id: 'food_cravings',
    text: 'Do you experience strong food cravings?',
    inputType: 'select',
    options: ['Never', 'Rarely', 'Sometimes', 'Often', 'Always'],
    required: true
  },
  {
    id: 'emotional_eating',
    text: 'Do you tend to eat when stressed or emotional?',
    inputType: 'select',
    options: ['Never', 'Rarely', 'Sometimes', 'Often', 'Always']
  }
];

// ============================================================================
// Sections
// ============================================================================

const SAFETY_SECTION: IntakeSection = {
  id: 'safety_screening',
  title: 'Critical Safety Screening',
  description: 'These questions are essential for your safety - GLP-1 medications have specific contraindications.',
  icon: 'alert-triangle',
  questions: SAFETY_SCREENING_QUESTIONS
};

const METABOLIC_SECTION: IntakeSection = {
  id: 'metabolic_assessment',
  title: 'Weight & Metabolic Assessment',
  description: 'Information about your current weight and weight loss history.',
  icon: 'scale',
  questions: METABOLIC_ASSESSMENT_QUESTIONS
};

const DIABETES_SECTION: IntakeSection = {
  id: 'diabetes_screening',
  title: 'Diabetes & Blood Sugar',
  description: 'Important for medication selection and safety monitoring.',
  icon: 'droplet',
  questions: DIABETES_QUESTIONS
};

const MEDICAL_SECTION: IntakeSection = {
  id: 'medical_history',
  title: 'Medical History',
  description: 'Other relevant medical conditions.',
  icon: 'clipboard',
  questions: MEDICAL_HISTORY_QUESTIONS
};

const SYMPTOMS_SECTION: IntakeSection = {
  id: 'symptoms',
  title: 'Current Symptoms & Eating Patterns',
  description: 'Understanding your current symptoms and eating behaviors.',
  icon: 'activity',
  questions: SYMPTOMS_QUESTIONS
};

const SHIPPING_BILLING_SECTION: IntakeSection = {
  id: 'shipping_billing_address',
  title: 'Shipping & Billing Address',
  description: 'Where should we ship your medications and send billing?',
  icon: 'package',
  questions: [
    {
      id: 'shipping_address_line1',
      text: 'Shipping Street Address',
      inputType: 'text',
      required: true,
    },
    {
      id: 'shipping_address_line2',
      text: 'Shipping Apt / Suite / Unit',
      inputType: 'text',
    },
    {
      id: 'shipping_city',
      text: 'Shipping City',
      inputType: 'text',
      required: true,
    },
    {
      id: 'shipping_state',
      text: 'Shipping State',
      inputType: 'select',
      options: [
        'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut',
        'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa',
        'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan',
        'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire',
        'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio',
        'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
        'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia',
        'Wisconsin', 'Wyoming', 'District of Columbia',
      ],
      required: true,
    },
    {
      id: 'shipping_postal_code',
      text: 'Shipping ZIP Code',
      inputType: 'text',
      required: true,
      validationRules: [
        { type: 'pattern', value: '^\\d{5}(-\\d{4})?$', message: 'Please enter a valid ZIP code' }
      ],
    },
    {
      id: 'billing_same_as_shipping',
      text: 'Is your billing address the same as your shipping address?',
      inputType: 'boolean',
      required: true,
    },
    {
      id: 'billing_address_line1',
      text: 'Billing Street Address',
      inputType: 'text',
      conditionalOn: {
        questionId: 'billing_same_as_shipping',
        operator: 'equals',
        value: false,
      },
    },
    {
      id: 'billing_address_line2',
      text: 'Billing Apt / Suite / Unit',
      inputType: 'text',
      conditionalOn: {
        questionId: 'billing_same_as_shipping',
        operator: 'equals',
        value: false,
      },
    },
    {
      id: 'billing_city',
      text: 'Billing City',
      inputType: 'text',
      conditionalOn: {
        questionId: 'billing_same_as_shipping',
        operator: 'equals',
        value: false,
      },
    },
    {
      id: 'billing_state',
      text: 'Billing State',
      inputType: 'select',
      options: [
        'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut',
        'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa',
        'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan',
        'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire',
        'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio',
        'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
        'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia',
        'Wisconsin', 'Wyoming', 'District of Columbia',
      ],
      conditionalOn: {
        questionId: 'billing_same_as_shipping',
        operator: 'equals',
        value: false,
      },
    },
    {
      id: 'billing_postal_code',
      text: 'Billing ZIP Code',
      inputType: 'text',
      conditionalOn: {
        questionId: 'billing_same_as_shipping',
        operator: 'equals',
        value: false,
      },
      validationRules: [
        { type: 'pattern', value: '^\\d{5}(-\\d{4})?$', message: 'Please enter a valid ZIP code' }
      ],
    },
  ]
};

const GOALS_SECTION: IntakeSection = {
  id: 'goals',
  title: 'Treatment Goals',
  description: 'What would you like to achieve?',
  icon: 'target',
  questions: [
    {
      id: 'primary_goals',
      text: 'What are your primary goals? (Select all that apply)',
      inputType: 'multiselect',
      options: [
        'Weight loss',
        'Blood sugar control',
        'Reduce appetite/cravings',
        'Improve energy',
        'Reduce cardiovascular risk',
        'Improve overall health'
      ],
      required: true
    },
    {
      id: 'timeline_expectations',
      text: 'What is your expected timeline for results?',
      inputType: 'select',
      options: [
        '1-3 months',
        '3-6 months',
        '6-12 months',
        '12+ months',
        'No specific timeline'
      ]
    },
    {
      id: 'medication_preference',
      text: 'Do you have a preference for which medication?',
      inputType: 'select',
      options: [
        'No preference - provider recommendation',
        'Semaglutide (Ozempic/Wegovy)',
        'Tirzepatide (Mounjaro/Zepbound)',
        'Prefer compounded option if available'
      ]
    },
    {
      id: 'additional_questions',
      text: 'Any questions or concerns for your provider?',
      inputType: 'text'
    }
  ]
};

// ============================================================================
// Complete Template
// ============================================================================

export const METABOLIC_TEMPLATE: IntakeTemplate = {
  id: 'metabolic_intake',
  templateType: 'metabolic',
  name: 'GLP-1 / Weight Management Intake',
  description: 'Comprehensive intake for GLP-1 receptor agonist therapy (Semaglutide, Tirzepatide). Includes critical MTC/MEN2 safety screening and metabolic assessment.',
  estimatedTime: '8-12 minutes',
  sections: [
    SAFETY_SECTION,
    METABOLIC_SECTION,
    DIABETES_SECTION,
    MEDICAL_SECTION,
    SYMPTOMS_SECTION,
    SHIPPING_BILLING_SECTION,
    GOALS_SECTION
  ]
};

/**
 * Check for critical contraindications
 */
export function checkContraindications(responses: Record<string, boolean | string>): {
  hasAbsoluteContraindication: boolean;
  hasRelativeContraindication: boolean;
  contraindications: string[];
  warnings: string[];
} {
  const contraindications: string[] = [];
  const warnings: string[] = [];

  // Absolute contraindications
  if (responses.mtc_personal_history === true) {
    contraindications.push('Personal history of medullary thyroid carcinoma (MTC)');
  }
  if (responses.men2_history === true) {
    contraindications.push('Multiple Endocrine Neoplasia syndrome type 2 (MEN2)');
  }
  if (responses.family_mtc_history === true) {
    contraindications.push('Family history of medullary thyroid carcinoma');
  }

  // Relative contraindications / warnings
  if (responses.pancreatitis_history === true) {
    warnings.push('History of pancreatitis - requires careful monitoring');
  }
  if (responses.gallbladder_disease === true) {
    warnings.push('Gallbladder disease - increased risk of complications');
  }
  if (responses.gastroparesis === true) {
    warnings.push('Gastroparesis - may affect tolerability');
  }
  if (responses.diabetes_status === 'Type 1 Diabetes') {
    warnings.push('Type 1 Diabetes - GLP-1 is not indicated for T1DM');
  }
  if (responses.eating_disorder_history === true) {
    warnings.push('History of eating disorder - requires psychological evaluation');
  }

  return {
    hasAbsoluteContraindication: contraindications.length > 0,
    hasRelativeContraindication: warnings.length > 0,
    contraindications,
    warnings
  };
}

/**
 * Calculate BMI from responses
 */
export function calculateBMI(
  weightLbs: number,
  heightFeet: number,
  heightInches: number
): {
  bmi: number;
  category: string;
} {
  const totalInches = (heightFeet * 12) + heightInches;
  const bmi = (weightLbs / (totalInches * totalInches)) * 703;
  
  let category: string;
  if (bmi < 18.5) {
    category = 'Underweight';
  } else if (bmi < 25) {
    category = 'Normal weight';
  } else if (bmi < 30) {
    category = 'Overweight';
  } else if (bmi < 35) {
    category = 'Obesity Class I';
  } else if (bmi < 40) {
    category = 'Obesity Class II';
  } else {
    category = 'Obesity Class III';
  }

  return {
    bmi: Math.round(bmi * 10) / 10,
    category
  };
}

export default METABOLIC_TEMPLATE;
